import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Enforce a single open row within an `.accordion` group. The click fires
    // before the native toggle, so `this.details.open === false` means this row
    // is about to open — at which point we close any sibling rows.
    if (!this.details.open) {
      this.#closeSiblings();
    }
  };

  /**
   * Closes other open accordion rows within the same `.accordion` group so that
   * at most one row is open at a time.
   */
  #closeSiblings() {
    const group = this.closest('.accordion');
    if (!group) return;

    for (const sibling of group.querySelectorAll('accordion-custom')) {
      if (sibling === this) continue;
      const details = sibling.querySelector('details');
      if (details instanceof HTMLDetailsElement && details.open) this.#animateClose(details);
    }
  }

  /**
   * Closes an open sibling row with an animation. Because the row losing focus
   * is not `:focus-within`, the stylesheet would normally disable its transition
   * (so it would snap shut). Tagging it with `is-closing` re-enables the
   * transition for the duration of the collapse, then removes the marker.
   * @param {HTMLDetailsElement} details - The sibling `<details>` to close.
   */
  #animateClose(details) {
    details.classList.add('is-closing');
    details.open = false;

    const cleanup = () => {
      details.classList.remove('is-closing');
      details.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (/** @type {TransitionEvent} */ event) => {
      // Only react to the size transition ending on the content itself.
      if (event.propertyName === 'block-size') cleanup();
    };

    details.addEventListener('transitionend', onEnd);
    // Fallback in case `transitionend` never fires (e.g. reduced motion).
    setTimeout(cleanup, 400);
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
